"use strict";
/**
 * Good for stripping an object of undefined values before inserting to firestore
 * @param object
 * @returns a new object with undefined values removed
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.flattenWithDepth = exports.isRecursableObject = void 0;
exports.stripUndefined = stripUndefined;
exports.stripUndefinedRecursive = stripUndefinedRecursive;
exports.stripValue = stripValue;
exports.isStrippableObject = isStrippableObject;
exports.stripValueRecursive = stripValueRecursive;
exports.recurse = recurse;
exports.flatten = flatten;
exports.getAttributeDifferences = getAttributeDifferences;
const ramda_1 = require("ramda");
const checks_1 = require("./checks");
const objectOperations_1 = require("./objectOperations");
function stripUndefined(object) {
    return stripValue(object, undefined);
}
function stripUndefinedRecursive(object) {
    return stripValueRecursive(object, undefined);
}
function stripValue(object, value) {
    const notValue = (v) => v !== value;
    if ((0, checks_1.isArray)(object)) {
        return (0, ramda_1.filter)(notValue, object);
    }
    return (0, ramda_1.pickBy)(notValue, object);
}
function isStrippableObject(unk) {
    return (typeof unk === 'object' &&
        typeof unk !== 'function' &&
        unk !== null &&
        !(unk instanceof Date) &&
        !(unk instanceof RegExp) &&
        !(0, checks_1.isArray)(unk));
}
function stripValueRecursive(object, valueToStrip) {
    const shallowStripped = stripValue(object, valueToStrip);
    const stripValueDeep = (v) => {
        if ((0, checks_1.isArray)(v)) {
            return v.map(value => {
                if ((0, checks_1.isArray)(value) || isStrippableObject(value)) {
                    return stripValueRecursive(value, valueToStrip);
                }
                return value;
            });
        }
        else if (isStrippableObject(v)) {
            return stripValueRecursive(v, valueToStrip);
        }
        return v;
    };
    if (isStrippableObject(shallowStripped)) {
        const stripValuesDeep = (0, ramda_1.mapObjIndexed)(stripValueDeep);
        return stripValuesDeep(shallowStripped);
    }
    else {
        return stripValueDeep(shallowStripped);
    }
}
/**
 * Function to recursively traverse an object of unknown type, typical uses include finding a nested objects of type T
 * and/or replacing objects of type T
 *
 * @param obj -- An object that might include nested object(s) of type T
 * @param isOneOf --  Callback function that returns true of the given object is of type T otherwise false
 * @param callback -- Optional callback function for each object found of type T (can be used to replace values)
 * @param context -- Optional context for callback()
 *
 * @returns A list of objects found that meet the criteria in isOneOf() or an empty array
 */
function recurse(obj, isOneOf, callback, context) {
    const collector = [];
    if (obj instanceof Object) {
        const children = _children(obj);
        if (children.length > 0) {
            children.forEach(child => {
                const r = recurse(child, isOneOf, callback, context);
                collector.push(...r);
            });
        }
        else if (!(0, checks_1.isArray)(obj)) {
            if (isOneOf(obj)) {
                if (callback) {
                    callback(obj, context);
                }
                collector.push(obj);
            }
        }
    }
    return collector;
}
function _children(obj) {
    const children = [];
    if (obj) {
        Object.keys(obj).forEach((k) => {
            if (obj[k] instanceof Object) {
                children.push(obj[k]);
            }
        });
    }
    return children;
}
const isRecursableObject = (value) => typeof value === 'object' && !Array.isArray(value) && value !== null;
exports.isRecursableObject = isRecursableObject;
function flatten(obj) {
    let newObj = {};
    const keys = Object.keys(obj);
    for (const key of keys) {
        const value = obj[key];
        if (!(0, exports.isRecursableObject)(value)) {
            newObj[key] = value;
        }
        else {
            newObj = { ...newObj, ...flatten(value) };
        }
    }
    return newObj;
}
const flattenWithDepth = (obj) => {
    const flattenWithDepthHelper = (obj, prefixStack) => {
        return Object.keys(obj).reduce((previous, current) => {
            const keyStack = [...prefixStack, current];
            const value = obj[current];
            if ((0, exports.isRecursableObject)(value)) {
                return { ...previous, ...flattenWithDepthHelper(value, keyStack) };
            }
            const key = keyStack.join('-');
            previous[key] = value;
            return previous;
        }, {});
    };
    return flattenWithDepthHelper(obj, []);
};
exports.flattenWithDepth = flattenWithDepth;
function getAttributeDifferences(before, after) {
    const beforeFlat = (0, exports.flattenWithDepth)(before);
    const afterFlat = (0, exports.flattenWithDepth)(after);
    const beforeKeys = beforeFlat ? Object.keys(beforeFlat) : [];
    const afterKeys = afterFlat ? Object.keys(afterFlat) : [];
    let keys = [...new Set([...beforeKeys, ...afterKeys])]; //get unique keys
    const diff = {};
    keys.forEach(key => {
        if ((0, objectOperations_1.hasChanged)(key, { before: beforeFlat, after: afterFlat }))
            diff[key] = { before: beforeFlat[key], after: afterFlat[key] };
    });
    return diff;
}
